import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Image as ImageIcon, Plus, Trash2, Upload, X, ChevronUp, ChevronDown,
  Eye, EyeOff, Edit, Loader2,
} from "lucide-react";
import LinkPicker from "@/components/LinkPicker";

const EMPTY_SLIDE = {
  image_url: "", image_url_mobile: "",
  title: "", subtitle: "", button_text: "",
  link_type: "category", link_target: "",
  overlay_opacity: 0.35, text_align: "left", text_color: "#FFFFFF",
  sort_order: 0, is_active: true,
};
const EMPTY_CARD = {
  image_url: "", title: "", subtitle: "",
  link_type: "category", link_target: "",
  size: "small", sort_order: 0, is_active: true,
};

export default function HomepageManager() {
  const [tab, setTab] = useState("slides");
  return (
    <div data-testid="homepage-manager" className="space-y-6">
      <div className="flex items-end justify-between border-b border-border pb-4">
        <div>
          <p className="text-[10px] uppercase tracking-[0.3em] text-[#C8A96A] mb-2">Anasayfa</p>
          <h1 className="font-heading text-3xl">Anasayfa Yönetimi</h1>
          <p className="text-sm text-[#52525B] mt-1">Slider ve kategori kartlarını anasayfaya dinamik olarak ekleyin.</p>
        </div>
        <div className="flex items-center gap-2">
          <TabBtn active={tab === "slides"} onClick={() => setTab("slides")} testid="homepage-tab-slides">Hero Slider</TabBtn>
          <TabBtn active={tab === "cards"} onClick={() => setTab("cards")} testid="homepage-tab-cards">Kategori Kartları</TabBtn>
        </div>
      </div>
      {tab === "slides" ? <SlidesPanel /> : <CardsPanel />}
    </div>
  );
}

function TabBtn({ active, onClick, children, testid }) {
  return (
    <button data-testid={testid} onClick={onClick}
      className={"px-4 py-2 text-[11px] uppercase tracking-[0.2em] border " + (active ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "border-border hover:bg-[#FAF8F3]")}>
      {children}
    </button>
  );
}

// ===== Hero Slides =====
function SlidesPanel() {
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null);

  const reload = () => api.get("/admin/homepage/hero-slides").then((r) => setList(r.data || []));
  useEffect(() => { reload(); }, []);

  const move = async (idx, dir) => {
    const next = [...list];
    const j = idx + dir;
    if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j], next[idx]];
    setList(next);
    await api.post("/admin/homepage/hero-slides/reorder", { order: next.map((x) => x.id) });
  };

  const toggleActive = async (s) => {
    await api.put(`/admin/homepage/hero-slides/${s.id}`, { ...s, is_active: !s.is_active });
    toast.success(!s.is_active ? "Slayt aktif" : "Slayt pasif");
    reload();
  };

  const remove = async (s) => {
    if (!window.confirm(`"${s.title || "Bu slayt"}" kalıcı olarak silinecek. Devam edilsin mi?`)) return;
    await api.delete(`/admin/homepage/hero-slides/${s.id}`);
    toast.success("Slayt silindi");
    reload();
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <p className="text-sm text-[#52525B]"><strong className="font-finance text-[#0A0A0A]">{list.length}</strong> slayt</p>
        <button data-testid="new-slide-btn" onClick={() => setEditing({ ...EMPTY_SLIDE, sort_order: list.length })}
          className="bg-[#0A0A0A] hover:bg-[#C8A96A] text-white px-5 py-2.5 text-xs uppercase tracking-[0.2em] inline-flex items-center gap-2">
          <Plus size={14} /> Yeni Slayt
        </button>
      </div>

      {list.length === 0 ? (
        <EmptyState icon={ImageIcon} text="Henüz slayt eklenmemiş. Yeni slayt ekleyerek anasayfa hero alanını özelleştirin." />
      ) : (
        <div className="space-y-3">
          {list.map((s, idx) => (
            <div key={s.id} data-testid={`slide-row-${s.id}`} className="flex gap-4 border border-border bg-white p-3">
              <div className="w-40 h-24 bg-[#FAF8F3] flex-shrink-0 overflow-hidden">
                {s.image_url ? <img src={s.image_url} alt="" className="w-full h-full object-cover" /> : <ImageIcon className="m-auto mt-8 text-[#A1A1AA]" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="font-heading text-lg truncate">{s.title || "(Başlıksız)"}</h3>
                  {s.is_active ? <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 bg-[#16A34A]/10 text-[#16A34A]">Aktif</span>
                                : <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 bg-[#71717A]/10 text-[#71717A]">Pasif</span>}
                </div>
                <p className="text-xs text-[#71717A] truncate">{s.subtitle}</p>
                <p className="text-[10px] uppercase tracking-wider text-[#52525B] mt-1">{s.link_type} {s.link_target ? "→ " + s.link_target.slice(0, 40) : ""}</p>
              </div>
              <div className="flex flex-col items-end justify-between gap-1">
                <div className="flex items-center gap-1">
                  <IconBtn onClick={() => move(idx, -1)} disabled={idx === 0} title="Yukarı" testid={`slide-up-${s.id}`}><ChevronUp size={14} /></IconBtn>
                  <IconBtn onClick={() => move(idx, 1)} disabled={idx === list.length - 1} title="Aşağı" testid={`slide-down-${s.id}`}><ChevronDown size={14} /></IconBtn>
                </div>
                <div className="flex items-center gap-1">
                  <IconBtn onClick={() => toggleActive(s)} title={s.is_active ? "Pasif yap" : "Aktif yap"} testid={`slide-toggle-${s.id}`}>
                    {s.is_active ? <Eye size={14} /> : <EyeOff size={14} />}
                  </IconBtn>
                  <IconBtn onClick={() => setEditing(s)} title="Düzenle" testid={`slide-edit-${s.id}`}><Edit size={14} /></IconBtn>
                  <IconBtn onClick={() => remove(s)} danger title="Sil" testid={`slide-delete-${s.id}`}><Trash2 size={14} /></IconBtn>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <SlideEditor slide={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); reload(); }} />
      )}
    </div>
  );
}

function SlideEditor({ slide, onClose, onSaved }) {
  const [form, setForm] = useState(slide);
  const [busy, setBusy] = useState(false);
  const isNew = !slide.id;

  const upd = (patch) => setForm((f) => ({ ...f, ...patch }));

  const save = async () => {
    if (!form.image_url) { toast.error("Görsel zorunlu"); return; }
    setBusy(true);
    try {
      if (isNew) await api.post("/admin/homepage/hero-slides", form);
      else await api.put(`/admin/homepage/hero-slides/${form.id}`, form);
      toast.success(isNew ? "Slayt eklendi" : "Kaydedildi");
      onSaved();
    } catch (e) {
      toast.error("Kayıt hatası: " + (e?.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div data-testid="slide-editor" className="bg-white w-full max-w-3xl max-h-[90vh] overflow-y-auto shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-border flex items-center justify-between bg-[#FAF8F3]">
          <h3 className="font-heading text-xl">{isNew ? "Yeni Slayt" : "Slayt Düzenle"}</h3>
          <button onClick={onClose} className="p-2 hover:bg-white"><X size={18} /></button>
        </div>
        <div className="p-6 space-y-5">
          <Field label="Masaüstü Görsel * (önerilen 2400×1050)" testid="slide-image-field">
            <ImagePicker value={form.image_url} onChange={(u) => upd({ image_url: u })} testidPrefix="slide-desktop-img" />
          </Field>
          <Field label="Mobil Görsel (isteğe bağlı, dikey için 900×1200)" testid="slide-image-mobile-field">
            <ImagePicker value={form.image_url_mobile} onChange={(u) => upd({ image_url_mobile: u })} testidPrefix="slide-mobile-img" />
          </Field>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Başlık">
              <input data-testid="slide-title" value={form.title || ""} onChange={(e) => upd({ title: e.target.value })}
                className="w-full border border-border px-3 py-2 text-sm" placeholder="Zarafetin ve Geleneğin Buluştuğu Yer" />
            </Field>
            <Field label="Buton Metni">
              <input data-testid="slide-button-text" value={form.button_text || ""} onChange={(e) => upd({ button_text: e.target.value })}
                className="w-full border border-border px-3 py-2 text-sm" placeholder="Koleksiyonu Keşfet" />
            </Field>
          </div>
          <Field label="Alt Başlık">
            <textarea data-testid="slide-subtitle" value={form.subtitle || ""} onChange={(e) => upd({ subtitle: e.target.value })} rows={2}
              className="w-full border border-border px-3 py-2 text-sm" placeholder="Üç nesildir Trabzon'da el işçiliği ile mücevher üretiyoruz." />
          </Field>
          <Field label="Bağlantı (Buton tıklandığında nereye gitsin?)">
            <LinkPicker value={{ link_type: form.link_type, link_target: form.link_target }}
              onChange={(v) => upd(v)} allowNone={true} />
          </Field>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Field label="Metin Hizası">
              <select data-testid="slide-text-align" value={form.text_align || "left"} onChange={(e) => upd({ text_align: e.target.value })}
                className="w-full border border-border px-3 py-2 text-sm bg-white">
                <option value="left">Sol</option>
                <option value="center">Orta</option>
                <option value="right">Sağ</option>
              </select>
            </Field>
            <Field label="Metin Rengi">
              <input data-testid="slide-text-color" type="color" value={form.text_color || "#FFFFFF"} onChange={(e) => upd({ text_color: e.target.value })}
                className="w-full h-10 border border-border" />
            </Field>
            <Field label={`Overlay Opaklık (${Math.round((form.overlay_opacity ?? 0.35) * 100)}%)`}>
              <input data-testid="slide-overlay" type="range" min="0" max="0.8" step="0.05"
                value={form.overlay_opacity ?? 0.35} onChange={(e) => upd({ overlay_opacity: parseFloat(e.target.value) })}
                className="w-full" />
            </Field>
          </div>
          <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
            <input data-testid="slide-active" type="checkbox" checked={!!form.is_active}
              onChange={(e) => upd({ is_active: e.target.checked })} className="h-4 w-4" />
            Aktif (anasayfada göster)
          </label>
        </div>
        <div className="px-5 py-3 border-t border-border flex items-center justify-end gap-2 bg-[#FAF8F3]">
          <button onClick={onClose} className="px-4 py-2 text-xs uppercase tracking-[0.2em] border border-border hover:bg-white">İptal</button>
          <button data-testid="slide-save-btn" onClick={save} disabled={busy}
            className="px-5 py-2 text-xs uppercase tracking-[0.2em] bg-[#0A0A0A] hover:bg-[#C8A96A] text-white inline-flex items-center gap-2 disabled:opacity-50">
            {busy ? <Loader2 size={12} className="animate-spin" /> : null} Kaydet
          </button>
        </div>
      </div>
    </div>
  );
}

// ===== Category Cards =====
function CardsPanel() {
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null);

  const reload = () => api.get("/admin/homepage/category-cards").then((r) => setList(r.data || []));
  useEffect(() => { reload(); }, []);

  const move = async (idx, dir) => {
    const next = [...list];
    const j = idx + dir;
    if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j], next[idx]];
    setList(next);
    await api.post("/admin/homepage/category-cards/reorder", { order: next.map((x) => x.id) });
  };

  const toggleActive = async (c) => {
    await api.put(`/admin/homepage/category-cards/${c.id}`, { ...c, is_active: !c.is_active });
    toast.success(!c.is_active ? "Kart aktif" : "Kart pasif");
    reload();
  };

  const remove = async (c) => {
    if (!window.confirm(`"${c.title}" kartı kalıcı olarak silinecek. Devam edilsin mi?`)) return;
    await api.delete(`/admin/homepage/category-cards/${c.id}`);
    toast.success("Kart silindi");
    reload();
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <p className="text-sm text-[#52525B]"><strong className="font-finance text-[#0A0A0A]">{list.length}</strong> kart</p>
        <button data-testid="new-card-btn" onClick={() => setEditing({ ...EMPTY_CARD, sort_order: list.length })}
          className="bg-[#0A0A0A] hover:bg-[#C8A96A] text-white px-5 py-2.5 text-xs uppercase tracking-[0.2em] inline-flex items-center gap-2">
          <Plus size={14} /> Yeni Kart
        </button>
      </div>

      {list.length === 0 ? (
        <EmptyState icon={ImageIcon} text="Henüz kategori kartı eklenmemiş. Hardcoded kategori bloğu yerine kullanılır." />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {list.map((c, idx) => (
            <div key={c.id} data-testid={`card-row-${c.id}`} className="border border-border bg-white">
              <div className="aspect-[16/9] bg-[#FAF8F3] overflow-hidden">
                {c.image_url ? <img src={c.image_url} alt="" className="w-full h-full object-cover" /> : <ImageIcon className="m-auto mt-12 text-[#A1A1AA]" />}
              </div>
              <div className="p-3 flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <h3 className="font-heading text-base truncate">{c.title}</h3>
                  <p className="text-xs text-[#71717A] truncate">{c.subtitle}</p>
                  <p className="text-[10px] uppercase tracking-wider text-[#52525B] mt-1">
                    {c.size === "large" ? "Büyük" : "Küçük"} · {c.link_type} {c.link_target ? "→ " + c.link_target.slice(0, 20) : ""}
                  </p>
                  {!c.is_active && <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 bg-[#71717A]/10 text-[#71717A] mt-1 inline-block">Pasif</span>}
                </div>
                <div className="flex flex-col items-end gap-1">
                  <div className="flex items-center gap-1">
                    <IconBtn onClick={() => move(idx, -1)} disabled={idx === 0} title="Yukarı" testid={`card-up-${c.id}`}><ChevronUp size={14} /></IconBtn>
                    <IconBtn onClick={() => move(idx, 1)} disabled={idx === list.length - 1} title="Aşağı" testid={`card-down-${c.id}`}><ChevronDown size={14} /></IconBtn>
                  </div>
                  <div className="flex items-center gap-1">
                    <IconBtn onClick={() => toggleActive(c)} title={c.is_active ? "Pasif yap" : "Aktif yap"} testid={`card-toggle-${c.id}`}>
                      {c.is_active ? <Eye size={14} /> : <EyeOff size={14} />}
                    </IconBtn>
                    <IconBtn onClick={() => setEditing(c)} title="Düzenle" testid={`card-edit-${c.id}`}><Edit size={14} /></IconBtn>
                    <IconBtn onClick={() => remove(c)} danger title="Sil" testid={`card-delete-${c.id}`}><Trash2 size={14} /></IconBtn>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <CardEditor card={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); reload(); }} />
      )}
    </div>
  );
}

function CardEditor({ card, onClose, onSaved }) {
  const [form, setForm] = useState(card);
  const [busy, setBusy] = useState(false);
  const isNew = !card.id;
  const upd = (patch) => setForm((f) => ({ ...f, ...patch }));

  const save = async () => {
    if (!form.image_url) { toast.error("Görsel zorunlu"); return; }
    if (!form.title) { toast.error("Başlık zorunlu"); return; }
    setBusy(true);
    try {
      if (isNew) await api.post("/admin/homepage/category-cards", form);
      else await api.put(`/admin/homepage/category-cards/${form.id}`, form);
      toast.success(isNew ? "Kart eklendi" : "Kaydedildi");
      onSaved();
    } catch (e) {
      toast.error("Kayıt hatası: " + (e?.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div data-testid="card-editor" className="bg-white w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-border flex items-center justify-between bg-[#FAF8F3]">
          <h3 className="font-heading text-xl">{isNew ? "Yeni Kart" : "Kart Düzenle"}</h3>
          <button onClick={onClose} className="p-2 hover:bg-white"><X size={18} /></button>
        </div>
        <div className="p-6 space-y-5">
          <Field label="Görsel *">
            <ImagePicker value={form.image_url} onChange={(u) => upd({ image_url: u })} testidPrefix="card-img" />
          </Field>
          <Field label="Başlık *">
            <input data-testid="card-title" value={form.title || ""} onChange={(e) => upd({ title: e.target.value })}
              className="w-full border border-border px-3 py-2 text-sm" placeholder="Pırlanta Yüzükler" />
          </Field>
          <Field label="Alt Başlık">
            <input data-testid="card-subtitle" value={form.subtitle || ""} onChange={(e) => upd({ subtitle: e.target.value })}
              className="w-full border border-border px-3 py-2 text-sm" placeholder="Keşfet →" />
          </Field>
          <Field label="Bağlantı">
            <LinkPicker value={{ link_type: form.link_type, link_target: form.link_target }} onChange={(v) => upd(v)} allowNone={false} />
          </Field>
          <Field label="Kart Boyutu">
            <select data-testid="card-size" value={form.size} onChange={(e) => upd({ size: e.target.value })}
              className="w-full border border-border px-3 py-2 text-sm bg-white">
              <option value="large">Büyük (2 sütun genişliğinde, vurgu)</option>
              <option value="small">Küçük (standart)</option>
            </select>
          </Field>
          <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
            <input data-testid="card-active" type="checkbox" checked={!!form.is_active} onChange={(e) => upd({ is_active: e.target.checked })} className="h-4 w-4" />
            Aktif
          </label>
        </div>
        <div className="px-5 py-3 border-t border-border flex items-center justify-end gap-2 bg-[#FAF8F3]">
          <button onClick={onClose} className="px-4 py-2 text-xs uppercase tracking-[0.2em] border border-border hover:bg-white">İptal</button>
          <button data-testid="card-save-btn" onClick={save} disabled={busy}
            className="px-5 py-2 text-xs uppercase tracking-[0.2em] bg-[#0A0A0A] hover:bg-[#C8A96A] text-white inline-flex items-center gap-2 disabled:opacity-50">
            {busy ? <Loader2 size={12} className="animate-spin" /> : null} Kaydet
          </button>
        </div>
      </div>
    </div>
  );
}

// ===== Shared bits =====
function Field({ label, children, testid }) {
  return (
    <div data-testid={testid}>
      <label className="block text-[11px] uppercase tracking-[0.2em] text-[#52525B] mb-1.5">{label}</label>
      {children}
    </div>
  );
}

function IconBtn({ children, danger, onClick, disabled, title, testid }) {
  return (
    <button type="button" data-testid={testid} onClick={onClick} disabled={disabled} title={title}
      className={"p-1.5 border border-border transition disabled:opacity-30 disabled:cursor-not-allowed " + (danger ? "text-[#DC2626] hover:bg-[#DC2626] hover:text-white" : "hover:bg-[#FAF8F3]")}>
      {children}
    </button>
  );
}

function EmptyState({ icon: Icon, text }) {
  return (
    <div className="border border-dashed border-border p-12 text-center bg-[#FAF8F3]">
      <Icon size={32} className="mx-auto mb-3 text-[#A1A1AA]" />
      <p className="text-sm text-[#52525B]">{text}</p>
    </div>
  );
}

// ===== Image Picker (upload + media library) =====
function ImagePicker({ value, onChange, testidPrefix = "img" }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [showLib, setShowLib] = useState(false);

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(r.data.url);
      toast.success("Yüklendi");
    } catch (e) {
      toast.error("Yükleme hatası: " + (e?.response?.data?.detail || e.message));
    } finally { setUploading(false); }
  };

  return (
    <div className="space-y-2">
      {value ? (
        <div className="relative inline-block">
          <img src={value} alt="" className="h-32 w-auto object-cover border border-border" />
          <button type="button" onClick={() => onChange("")} data-testid={`${testidPrefix}-clear`}
            className="absolute top-1 right-1 bg-white/90 hover:bg-white p-1 shadow"><X size={12} /></button>
        </div>
      ) : (
        <div className="h-32 w-56 border border-dashed border-border flex items-center justify-center text-[#A1A1AA]">
          <ImageIcon size={24} />
        </div>
      )}
      <div className="flex items-center gap-2">
        <input ref={fileRef} type="file" accept="image/*" hidden onChange={(e) => upload(e.target.files?.[0])} />
        <button type="button" data-testid={`${testidPrefix}-upload`} onClick={() => fileRef.current?.click()} disabled={uploading}
          className="px-3 py-2 text-[11px] uppercase tracking-[0.18em] border border-border hover:bg-[#FAF8F3] inline-flex items-center gap-2 disabled:opacity-50">
          {uploading ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
          {uploading ? "Yükleniyor…" : "Yeni Yükle"}
        </button>
        <button type="button" data-testid={`${testidPrefix}-library`} onClick={() => setShowLib(true)}
          className="px-3 py-2 text-[11px] uppercase tracking-[0.18em] border border-border hover:bg-[#FAF8F3] inline-flex items-center gap-2">
          <ImageIcon size={12} /> Medya Kütüphanesi
        </button>
        <input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="veya URL yapıştır"
          className="flex-1 border border-border px-2 py-2 text-xs" data-testid={`${testidPrefix}-url`} />
      </div>
      {showLib && <MediaLibraryModal onClose={() => setShowLib(false)} onPick={(u) => { onChange(u); setShowLib(false); }} />}
    </div>
  );
}

function MediaLibraryModal({ onClose, onPick }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/admin/media").then((r) => { setItems(r.data || []); setLoading(false); }).catch(() => setLoading(false));
  }, []);
  return (
    <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4" onClick={onClose}>
      <div data-testid="media-library-modal" className="bg-white w-full max-w-4xl max-h-[80vh] overflow-hidden shadow-xl flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-border flex items-center justify-between bg-[#FAF8F3]">
          <h3 className="font-heading text-lg">Medya Kütüphanesi</h3>
          <button onClick={onClose} className="p-2 hover:bg-white"><X size={16} /></button>
        </div>
        <div className="p-5 overflow-y-auto">
          {loading ? <p className="text-sm text-[#52525B]">Yükleniyor…</p>
            : items.length === 0 ? <p className="text-sm text-[#52525B]">Henüz medya yok. Önce yeni bir görsel yükleyin.</p>
            : (
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3">
                {items.slice(0, 200).map((m) => (
                  <button key={m.id || m.url} type="button" onClick={() => onPick(m.url)} data-testid={`media-pick-${m.id || m.url}`}
                    className="aspect-square overflow-hidden border border-border hover:ring-2 hover:ring-[#C8A96A]">
                    <img src={m.url} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )
          }
        </div>
      </div>
    </div>
  );
}
